/**
 * DealDetail.jsx — Deal status and lifecycle management.
 *
 * Shows: deal status, buyer/bidder anonymous IDs, deal value, escrow, disputes, counterparty identity (after admin reveal on CLOSED deals).
 * Actions: ACTIVE→IN_PROGRESS; buyer **Mark deal completed** when every milestone is **buyer-accepted** (CONFIRMED or RELEASED), or no milestones / escrow **COMPLETED**. Party from **`viewer_is_buyer`** API field with fallbacks; CLOSED → rate; identity section as before.
 *
 * Requirements: 23.5
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiClient } from '../../utils/auth';
import { viewerHasRated } from '../../utils/ratingHint';
import ContractSigning from './ContractSigning';
import DisputePanel from '../Dispute/DisputePanel';
import PaymentSection from '../Payment/PaymentSection';

const STATUS_COLORS = {
  ACTIVE: 'info',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CLOSED: 'default',
  PAUSED_ARBITRATION: 'error',
};

const DISPUTE_STATUS_COLORS = {
  OPEN: 'warning',
  RESOLVED_BUYER: 'success',
  RESOLVED_BIDDER: 'success',
  ESCALATED: 'error',
  DISMISSED: 'default',
};

const ERROR_MESSAGES = {
  INVALID_DEAL_TRANSITION: 'This status transition is not allowed.',
  PERMISSION_DENIED: 'You do not have permission to perform this action.',
  ESCROW_MILESTONES_INCOMPLETE:
    'Finish all escrow milestones (bidder submit → buyer accept each one) before marking the deal completed.',
  MILESTONE_ORDER_VIOLATION:
    'Earlier milestones must be buyer-confirmed or released before this one. Finish buyer confirmation on the previous milestone first.',
  MILESTONE_STATUS_INVALID: 'This milestone is not in the right state for that action.',
  CONTRACT_NOT_COMPLETED: 'Work cannot begin until the contract has been signed by both parties and approved by admin.',
  PAYMENT_NOT_DEPOSITED: 'Work cannot begin until the buyer has deposited the payment.',
  IDENTITY_REVEAL_REQUIRES_CLOSED_DEAL:
    'Identity reveal is not available for this deal status.',
  IDENTITY_REVEAL_STATUS_NOT_ALLOWED:
    'Identity reveal can be requested only while the deal is active, in progress, or closed.',
  IDENTITY_ALREADY_RELEASED: 'Identities are already released for this deal.',
  VALIDATION_ERROR: 'Check your input and try again.',
  REJECTION_REASON_REQUIRED: 'Please provide a rejection reason of at least 10 characters.',
};

const MILESTONE_STATUS_COLORS = {
  PENDING: 'default',
  PENDING_CONFIRMATION: 'warning',
  CONFIRMED: 'info',
  RELEASED: 'success',
  DISPUTED: 'error',
};

function formatDeadlineInWeeks(deadline) {
  if (!deadline) return '—';
  const target = new Date(deadline);
  const now = new Date();
  const diffDays = Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
  const weeks = Math.max(1, Math.ceil(diffDays / 7));
  return `${weeks} week${weeks > 1 ? 's' : ''}`;
}

export default function DealDetail() {
  const { id: dealId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = typeof window !== 'undefined' ? localStorage.getItem('cmp_role') || '' : '';
  const viewerAnonStored =
    typeof window !== 'undefined' ? localStorage.getItem('cmp_anonymous_id') : null;

  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [milestoneLoadingId, setMilestoneLoadingId] = useState(null);
  const [retention, setRetention] = useState(null);
  const [revealRequested, setRevealRequested] = useState(false);
  const [revealLoading, setRevealLoading] = useState(false);
  const [rejectDialogMilestone, setRejectDialogMilestone] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const fetchDeal = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/v1/deals/${dealId}/`);
      setDeal(data);
      // Fetch retention window info
      try {
        const { data: ret } = await apiClient.get(`/api/v1/deals/${dealId}/retention/`);
        setRetention(ret);
      } catch { /* retention endpoint optional */ }
    } catch {
      setActionError('Failed to load deal details.');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchDeal();
  }, [fetchDeal, location.key]);

  const handleTransition = async (newStatus) => {
    setActionLoading(true);
    setActionError('');
    try {
      await apiClient.patch(`/api/v1/deals/${dealId}/status/`, { deal_status: newStatus });
      await fetchDeal();
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setActionError(ERROR_MESSAGES[code] || 'Failed to update deal status.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMilestoneComplete = async (escrowId, milestoneId) => {
    setMilestoneLoadingId(milestoneId);
    setActionError('');
    try {
      await apiClient.post(`/api/v1/escrow/${escrowId}/milestones/${milestoneId}/complete/`);
      await fetchDeal();
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const apiMessage = err.response?.data?.error?.message;
      const details = err.response?.data?.error?.details || {};
      let message = ERROR_MESSAGES[code] || apiMessage || 'Could not submit milestone as complete.';
      if (code === 'PAYMENT_NOT_DEPOSITED' && details.payment_status) {
        message = `${message} Current payment status: ${details.payment_status}.`;
      }
      if (code === 'CONTRACT_NOT_COMPLETED' && details.contract_status) {
        message = `${message} Contract status: ${details.contract_status}.`;
      }
      setActionError(message);
    } finally {
      setMilestoneLoadingId(null);
    }
  };

  const handleMilestoneConfirm = async (escrowId, milestoneId) => {
    setMilestoneLoadingId(milestoneId);
    setActionError('');
    try {
      await apiClient.post(`/api/v1/escrow/${escrowId}/milestones/${milestoneId}/confirm/`);
      await fetchDeal();
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setActionError(ERROR_MESSAGES[code] || 'Could not confirm milestone.');
    } finally {
      setMilestoneLoadingId(null);
    }
  };

  const openRejectDialog = (milestone) => {
    setRejectDialogMilestone(milestone);
    setRejectReason('');
    setRejectError('');
  };

  const closeRejectDialog = () => {
    setRejectDialogMilestone(null);
    setRejectReason('');
    setRejectError('');
  };

  const handleMilestoneReject = async () => {
    if (!rejectDialogMilestone || !deal?.escrow?.id) return;
    if (rejectReason.trim().length < 10) {
      setRejectError(ERROR_MESSAGES.REJECTION_REASON_REQUIRED);
      return;
    }
    setMilestoneLoadingId(rejectDialogMilestone.id);
    setRejectError('');
    setActionError('');
    try {
      await apiClient.post(
        `/api/v1/escrow/${deal.escrow.id}/milestones/${rejectDialogMilestone.id}/reject/`,
        { rejection_reason: rejectReason.trim() }
      );
      closeRejectDialog();
      await fetchDeal();
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setRejectError(ERROR_MESSAGES[code] || err.response?.data?.error?.message || 'Could not reject milestone.');
    } finally {
      setMilestoneLoadingId(null);
    }
  };

  const handleIdentityRevealRequest = async () => {
    setRevealLoading(true);
    setActionError('');
    try {
      await apiClient.post(`/api/v1/deals/${dealId}/request-identity-reveal/`, {});
      setRevealRequested(true);
      await fetchDeal();
    } catch (err) {
      const code = err.response?.data?.error?.code;
      if (code === 'IDENTITY_ALREADY_RELEASED') {
        setRevealRequested(true);
      } else {
        setActionError(
          err.response?.data?.error?.message ||
          'Could not submit identity reveal request. Note: this requires the deal to be CLOSED.'
        );
      }
    } finally {
      setRevealLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress aria-label="Loading deal" />
      </Box>
    );
  }

  if (!deal) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Deal not found.</Alert>
      </Box>
    );
  }

  const alreadyRated = viewerHasRated(deal, dealId);
  const disputes = Array.isArray(deal.disputes) ? deal.disputes : [];
  // API tells us party; fall back to anonymous id / role for older backends or tests.
  const isViewerBuyer =
    typeof deal.viewer_is_buyer === 'boolean'
      ? deal.viewer_is_buyer
      : (Boolean(viewerAnonStored) && String(deal.buyer_anonymous_id) === String(viewerAnonStored)) ||
        (!viewerAnonStored && role === 'BUYER');
  const isViewerBidder =
    typeof deal.viewer_is_buyer === 'boolean'
      ? !deal.viewer_is_buyer
      : (Boolean(viewerAnonStored) && String(deal.bidder_anonymous_id) === String(viewerAnonStored)) ||
        (!viewerAnonStored && role === 'BIDDER');
  const viewerAnon = isViewerBuyer ? deal.buyer_anonymous_id : deal.bidder_anonymous_id;
  const hasOpenDispute = disputes.some((d) => d.dispute_status === 'OPEN');
  const milestones = deal.escrow?.milestones ?? [];
  const hasAnyEscrow = Boolean(deal.escrow_id);
  const contractCompleted = deal.contract_status === 'COMPLETED';
  const paymentStatus = deal.payment_status || 'NOT_INITIATED';
  const paymentDeposited = ['CAPTURED', 'RELEASED', 'DISPUTE_RESOLVED'].includes(paymentStatus);
  const canBeginMilestoneWork = contractCompleted && paymentDeposited;
  const milestoneAccepted = (m) =>
    m.milestone_status === 'CONFIRMED' || m.milestone_status === 'RELEASED';
  const allMilestonesReleased =
    milestones.length > 0 && milestones.every((m) => m.milestone_status === 'RELEASED');
  const allMilestonesAccepted =
    milestones.length > 0 && milestones.every(milestoneAccepted);
  const acceptedMilestoneCount = milestones.filter(milestoneAccepted).length;

  // Buyer may complete the deal once every milestone is buyer-accepted (CONFIRMED or RELEASED).
  let escrowAllowsMarkCompleted = true;
  if (hasAnyEscrow && deal.escrow) {
    if (deal.escrow.escrow_status === 'COMPLETED') {
      escrowAllowsMarkCompleted = true;
    } else if (milestones.length === 0) {
      escrowAllowsMarkCompleted = true;
    } else {
      escrowAllowsMarkCompleted = allMilestonesAccepted;
    }
  }

  const buyerCanMarkDealCompleted =
    deal.deal_status === 'IN_PROGRESS' && isViewerBuyer && escrowAllowsMarkCompleted;

  const showDealMarkCompleted = buyerCanMarkDealCompleted;

  const escrowBlocksDealComplete =
    deal.deal_status === 'IN_PROGRESS' &&
    isViewerBuyer &&
    hasAnyEscrow &&
    deal.escrow &&
    milestones.length > 0 &&
    deal.escrow.escrow_status !== 'COMPLETED' &&
    !allMilestonesAccepted;

  const buyerAllMilestonesAcceptedAwaitingAdminRelease =
    deal.deal_status === 'IN_PROGRESS' &&
    isViewerBuyer &&
    allMilestonesAccepted &&
    !allMilestonesReleased;

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4, p: 3 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/deals/mine')}
        sx={{
          mb: 3,
          borderColor: 'rgba(99, 102, 241, 0.4)',
          color: '#818cf8',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
          }
        }}
      >
        Back to My Deals
      </Button>
      {/* ── Arbitration Paused Alert Banner ── */}
      {deal.deal_status === 'PAUSED_ARBITRATION' && (
        <Alert severity="error" sx={{ mb: 3, border: '1px solid #f87171', bgcolor: 'rgba(239, 68, 68, 0.05)' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            ⚠️ Ongoing Arbitration Active
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Work and escrow transactions are paused for this deal until the administrator issues a final decision.
          </Typography>
          {deal.active_arbitration_id && (
            <Button
              variant="contained"
              color="error"
              size="small"
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
              onClick={() => navigate(`/arbitrations/${deal.active_arbitration_id}`)}
            >
              Enter Arbitration Room
            </Button>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Deal #{dealId}</Typography>
        <Chip
          label={deal.deal_status}
          color={STATUS_COLORS[deal.deal_status] || 'default'}
        />
      </Box>

      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
        <Typography variant="body1">
          <strong>Deal Value:</strong> Rs.{Number(deal.deal_value).toLocaleString('en-IN')}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2" color="text.secondary">
          <strong>Buyer ID:</strong> {deal.buyer_anonymous_id}
        </Typography>
        {deal.anonymity_release_flag && deal.counterparty_identity && !isViewerBuyer && (
          <Box sx={{ ml: 2, mt: 0.5, mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.primary">
              👤 <strong>Revealed Buyer Name:</strong> {deal.counterparty_identity.name}
            </Typography>
            <Typography variant="body2" color="text.primary">
              ✉ <strong>Revealed Buyer Email:</strong> {deal.counterparty_identity.email}
            </Typography>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          <strong>Bidder ID:</strong> {deal.bidder_anonymous_id}
        </Typography>
        {deal.anonymity_release_flag && deal.counterparty_identity && isViewerBuyer && (
          <Box sx={{ ml: 2, mt: 0.5, mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.primary">
              👤 <strong>Revealed Bidder Name:</strong> {deal.counterparty_identity.name}
            </Typography>
            <Typography variant="body2" color="text.primary">
              ✉ <strong>Revealed Bidder Email:</strong> {deal.counterparty_identity.email}
            </Typography>
          </Box>
        )}
        {deal.escrow_available && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            ✓ Escrow available for this deal
          </Typography>
        )}
      </Paper>

      {disputes.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={2}>
          <Typography variant="subtitle1" gutterBottom>
            Disputes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Both parties on this deal see the same dispute history (anonymous IDs only). Status updates when an
            administrator resolves a dispute.
          </Typography>
          <DisputePanel
            dealId={dealId}
            disputes={disputes}
            viewerAnon={viewerAnon}
            viewerRole={role}
            onDisputeResolved={fetchDeal}
            retentionInfo={retention}
          />
        </Paper>
      )}

      {/* Retention window info — shown after deal COMPLETED */}
      {retention && deal.deal_status === 'COMPLETED' && (
        <Alert
          severity={retention.deal_dispute_window_open ? 'warning' : 'info'}
          sx={{ mb: 2 }}
        >
          {retention.deal_dispute_window_open
            ? `⏱ 2-day dispute window is open${retention.deal_dispute_window_expires ? ` — expires ${new Date(retention.deal_dispute_window_expires).toLocaleString()}` : ''}. Either party can raise a dispute before funds are released.`
            : 'The 2-day dispute window has closed. No further disputes can be raised on this deal.'}
        </Alert>
      )}

      {/* ── Contract Signing Section ── */}
      {(deal.deal_status === 'ACTIVE' || deal.deal_status === 'IN_PROGRESS') && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={1} variant="outlined">
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            📝 Contract Signing
          </Typography>
          <ContractSigning
            dealId={dealId}
            isBuyer={isViewerBuyer}
            onIdentityRevealRequest={handleIdentityRevealRequest}
            revealRequested={revealRequested || !!deal.viewer_identity_reveal_request?.requested_at}
            anonymityReleaseFlag={deal.anonymity_release_flag}
            revealLoading={revealLoading}
          />
          {/* Show identity reveal status after request */}
          {isViewerBuyer && !deal.anonymity_release_flag && (revealRequested || deal.viewer_identity_reveal_request?.requested_at) && (
            <Alert severity="success" sx={{ mt: 2 }}>
              ✅ Identity reveal request submitted. An administrator has been notified and will approve it shortly.
              Once approved, the bidder's name and email will be visible here.
            </Alert>
          )}
          {isViewerBuyer && deal.anonymity_release_flag && (
            <Alert severity="success" sx={{ mt: 2 }}>
              🎉 <strong>Identity reveal request approved!</strong> The bidder's real name and email are now revealed and displayed in the Deal Details section above.
            </Alert>
          )}
          {revealLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Submitting identity reveal request…</Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* ── Payment Section ── */}
      {contractCompleted && (deal.deal_status === 'ACTIVE' || deal.deal_status === 'IN_PROGRESS') && (
        <PaymentSection
          dealId={dealId}
          dealValue={deal.deal_value}
          isBuyer={isViewerBuyer}
          contractCompleted={contractCompleted}
          onPaymentUpdated={fetchDeal}
        />
      )}

      {deal.escrow_id && deal.escrow && milestones.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={1} variant="outlined">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Escrow Milestones
            </Typography>
            <Chip
              label={`${acceptedMilestoneCount}/${milestones.length} Accepted`}
              size="small"
              color={allMilestonesAccepted ? 'success' : 'default'}
            />
            {allMilestonesAccepted && !allMilestonesReleased && (
              <Chip
                label={`${milestones.filter((m) => m.milestone_status === 'RELEASED').length}/${milestones.length} Funds released`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>

          {/* Escrow flow explanation — tailored per role */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
            {isViewerBuyer ? (
              <Typography variant="body2" color="text.secondary">
                <strong>How escrow works for this deal:</strong> Each milestone is paid out separately.
                When the bidder completes a milestone, you review and confirm it. Once you confirm,
                the admin releases that milestone's funds to the bidder — <em>not the full deal amount at once</em>.
                You can only mark the deal completed after <strong>every milestone is RELEASED</strong>.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                <strong>How escrow works for this deal:</strong> Each milestone is paid separately.
                Submit your work for each milestone → buyer confirms → admin releases that milestone's
                payment to you. You receive funds milestone-by-milestone, not the full amount at once.
                Start the next milestone as soon as the buyer confirms the previous one.
              </Typography>
            )}
          </Paper>

          {milestones.map((ms) => (
            <Box
              key={ms.id}
              sx={{
                py: 1.5,
                px: 1,
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: ms.milestone_status === 'RELEASED' ? 'success.light'
                  : ms.milestone_status === 'CONFIRMED' ? 'info.light'
                  : ms.milestone_status === 'PENDING_CONFIRMATION' ? 'warning.light'
                  : ms.milestone_status === 'DISPUTED' ? 'error.light'
                  : 'divider',
                bgcolor: ms.milestone_status === 'RELEASED' ? 'success.50'
                  : ms.milestone_status === 'CONFIRMED' ? 'info.50'
                  : 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  Milestone {ms.sequence_order}: {ms.title}
                </Typography>
                <Chip
                  label={ms.milestone_status === 'PENDING' ? 'Not started'
                    : ms.milestone_status === 'PENDING_CONFIRMATION' ? 'Work submitted — awaiting your confirmation'
                    : ms.milestone_status === 'CONFIRMED' ? 'Confirmed — awaiting admin fund release'
                    : ms.milestone_status === 'RELEASED' ? '✓ Funds released'
                    : ms.milestone_status === 'DISPUTED' ? 'In dispute'
                    : ms.milestone_status}
                  size="small"
                  color={MILESTONE_STATUS_COLORS[ms.milestone_status] || 'default'}
                />
                <Typography variant="caption" color={ms.milestone_status === 'RELEASED' ? 'success.main' : 'text.secondary'} fontWeight={600}>
                  ₹{Number(ms.amount).toLocaleString('en-IN')}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Deadline: {formatDeadlineInWeeks(ms.deadline)}
              </Typography>

              {/* Bidder: rejected — show reason and allow resubmit */}
              {isViewerBidder && ms.milestone_status === 'PENDING' && ms.rejection_reason && (
                <Alert severity="warning" sx={{ mt: 0.5, py: 0.5 }}>
                  <strong>Buyer rejected your previous submission.</strong>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {ms.rejection_reason}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Address the feedback below and resubmit your work.
                  </Typography>
                </Alert>
              )}

              {/* Bidder: submit work */}
              {isViewerBidder && ms.milestone_status === 'PENDING' && (
                canBeginMilestoneWork ? (
                  <Button variant="outlined" size="small" sx={{ mt: 0.5 }}
                    disabled={milestoneLoadingId === ms.id || hasOpenDispute}
                    onClick={() => handleMilestoneComplete(deal.escrow.id, ms.id)}>
                    {milestoneLoadingId === ms.id ? <CircularProgress size={18} /> : (ms.rejection_reason ? 'Resubmit work for this milestone' : 'Submit work for this milestone')}
                  </Button>
                ) : !contractCompleted ? (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                    Contract must be completed before submitting milestones.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                    Waiting for buyer payment
                    {paymentStatus === 'ORDER_CREATED' ? ' verification' : ''} (status: {paymentStatus}).
                  </Typography>
                )
              )}

              {/* Bidder: work submitted, waiting for buyer */}
              {isViewerBidder && ms.milestone_status === 'PENDING_CONFIRMATION' && (
                <Alert severity="info" sx={{ mt: 0.5, py: 0.5 }}>
                  Your work has been submitted. Waiting for the buyer to confirm it.
                  Once confirmed, the admin will release ₹{Number(ms.amount).toLocaleString('en-IN')} to you.
                </Alert>
              )}

              {/* Bidder: confirmed, waiting for admin */}
              {isViewerBidder && ms.milestone_status === 'CONFIRMED' && (
                <Alert severity="success" sx={{ mt: 0.5, py: 0.5 }}>
                  ✅ Buyer confirmed your work. The admin is reviewing and will release
                  ₹{Number(ms.amount).toLocaleString('en-IN')} to you shortly.
                </Alert>
              )}

              {/* Buyer: accept or reject milestone submission */}
              {isViewerBuyer && ms.milestone_status === 'PENDING_CONFIRMATION' && (
                <Box sx={{ mt: 0.5 }}>
                  <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                    The bidder has submitted work for this milestone. Review it and <strong>accept</strong> if satisfied,
                    or <strong>reject</strong> with feedback so they can resubmit.
                    Accepting notifies the admin to release <strong>₹{Number(ms.amount).toLocaleString('en-IN')}</strong> to the bidder.
                  </Alert>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" size="small" color="success"
                      disabled={milestoneLoadingId === ms.id || hasOpenDispute}
                      onClick={() => handleMilestoneConfirm(deal.escrow.id, ms.id)}>
                      {milestoneLoadingId === ms.id ? <CircularProgress size={18} /> : 'Accept submission'}
                    </Button>
                    <Button variant="outlined" size="small" color="error"
                      disabled={milestoneLoadingId === ms.id || hasOpenDispute}
                      onClick={() => openRejectDialog(ms)}>
                      Reject submission
                    </Button>
                    <Button variant="outlined" size="small" color="warning"
                      onClick={() => navigate(`/disputes/raise/${dealId}?milestone_id=${ms.id}`)}>
                      Raise dispute
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Buyer: confirmed, waiting for admin release */}
              {isViewerBuyer && ms.milestone_status === 'CONFIRMED' && (() => {
                const msRet = retention?.milestones?.find((r) => r.milestone_id === ms.id);
                return (
                  <Box sx={{ mt: 0.5 }}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      You confirmed this milestone. The admin will release
                      <strong> ₹{Number(ms.amount).toLocaleString('en-IN')}</strong> to the bidder.
                      This does not affect the full deal amount — only this milestone's portion is released.
                    </Alert>
                    {msRet?.dispute_window_open && (
                      <Button variant="outlined" size="small" color="warning" sx={{ mt: 0.5 }}
                        onClick={() => navigate(`/disputes/raise/${dealId}?milestone_id=${ms.id}`)}>
                        Raise dispute before funds are released
                      </Button>
                    )}
                  </Box>
                );
              })()}

              {ms.milestone_status === 'RELEASED' && (
                <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
                  ✅ ₹{Number(ms.amount).toLocaleString('en-IN')} has been released to the bidder for this milestone.
                </Typography>
              )}

              {ms.milestone_status === 'DISPUTED' && (
                <Chip label="Disputed — funds held pending resolution" size="small" color="error" sx={{ mt: 0.5 }} />
              )}
            </Box>
          ))}
        </Paper>
      )}

      {deal.escrow_id && !deal.escrow && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Escrow #{deal.escrow_id} is configured; reload the page if milestone details do not appear.
        </Typography>
      )}

      {deal.escrow_id && deal.escrow && milestones.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Escrow #{deal.escrow_id} is configured for this deal (no milestones returned).
        </Typography>
      )}

      {deal.deal_status === 'COMPLETED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Deal is marked completed. An administrator will close it shortly — once closed, the rating window opens for both parties.
        </Alert>
      )}

      {deal.deal_status === 'CLOSED' && !deal.anonymity_release_flag && (
        <Paper sx={{ p: 2, mb: 2 }} elevation={1} variant="outlined">
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Identity reveal
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Request an administrator to release the other party&apos;s name and email for this closed deal.
          </Typography>
          <Button
            variant="outlined"
            disabled={revealLoading || revealRequested || !!deal.viewer_identity_reveal_request?.requested_at}
            onClick={handleIdentityRevealRequest}
          >
            {revealLoading ? <CircularProgress size={18} /> : 'Request Identity Reveal'}
          </Button>
          {(revealRequested || deal.viewer_identity_reveal_request?.requested_at) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your identity reveal request has been submitted. An administrator will review it shortly.
            </Alert>
          )}
        </Paper>
      )}

      {deal.deal_status === 'CLOSED' && !alreadyRated && (
        <Alert severity="success" sx={{ mb: 2 }}>
          This deal is closed. Rate the other party within <strong>7 days</strong> of close (one rating per deal).
        </Alert>
      )}

      {deal.deal_status === 'CLOSED' && alreadyRated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have already submitted your rating for this deal.
        </Alert>
      )}

      {/* Status transition buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Start Work — Bidder only, and only when escrow is NOT available AND contract is completed */}
        {deal.deal_status === 'ACTIVE' && isViewerBidder && !deal.escrow_available && (
          contractCompleted ? (
            <Button
              variant="contained"
              onClick={() => handleTransition('IN_PROGRESS')}
              disabled={actionLoading}
              aria-label="Start work on this deal"
            >
              {actionLoading ? <CircularProgress size={20} /> : 'Start Work'}
            </Button>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              You cannot start work until the contract has been signed by both parties and approved by admin.
              {deal.contract_status ? ` Contract status: ${deal.contract_status}` : ' No contract initiated yet.'}
            </Alert>
          )
        )}

        {/* Escrow deal — bidder sees info instead of Start Work button */}
        {deal.deal_status === 'ACTIVE' && isViewerBidder && deal.escrow_available && (
          canBeginMilestoneWork ? (
            <Alert severity="info" sx={{ width: '100%' }}>
              This deal uses escrow. To begin work, submit your <strong>first milestone</strong> below — that will automatically move the deal to In Progress.
            </Alert>
          ) : contractCompleted ? (
            <Alert severity="warning" sx={{ width: '100%' }}>
              Waiting for the buyer to complete payment (status: <strong>{paymentStatus}</strong>) before you can submit milestones.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              You cannot submit milestones until the contract has been signed by both parties and approved by admin.
              {deal.contract_status ? ` Contract status: ${deal.contract_status}` : ' No contract initiated yet.'}
            </Alert>
          )
        )}

        {/* Buyer never sees Start Work — show info only */}
        {deal.deal_status === 'ACTIVE' && isViewerBuyer && (
          <Alert severity="info" sx={{ width: '100%' }}>
            Waiting for the bidder to start work. You will be notified when work begins.
          </Alert>
        )}

        {showDealMarkCompleted && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleTransition('COMPLETED')}
            disabled={actionLoading}
            aria-label="Mark deal as completed"
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Mark deal completed'}
          </Button>
        )}

        {buyerAllMilestonesAcceptedAwaitingAdminRelease && (
          <Alert severity="info" sx={{ width: '100%' }}>
            You have accepted every milestone. You can mark the deal completed now.
            An administrator will still release escrow funds to the bidder separately (each milestone must reach RELEASED).
          </Alert>
        )}

        {deal.deal_status === 'IN_PROGRESS' && isViewerBuyer && escrowBlocksDealComplete && (
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
            {hasAnyEscrow && !deal.escrow
              ? 'Escrow is linked to this deal — reload the page if details are missing. After all milestones are accepted, use Mark deal completed.'
              : 'Finish every escrow milestone (bidder submit → you accept each one) before marking the deal completed.'}
          </Typography>
        )}

        {(deal.deal_status === 'ACTIVE' || deal.deal_status === 'IN_PROGRESS' ||
          (deal.deal_status === 'COMPLETED' && retention?.deal_dispute_window_open)) && !hasOpenDispute && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => navigate(`/disputes/raise/${dealId}`)}
            aria-label="Raise a dispute for this deal"
          >
            Raise a dispute
          </Button>
        )}

        {(deal.deal_status === 'ACTIVE' || deal.deal_status === 'IN_PROGRESS' ||
          (deal.deal_status === 'COMPLETED' && retention?.deal_dispute_window_open)) && hasOpenDispute && (
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%' }}>
            An open dispute is in progress — both parties see the same details in <strong>Disputes</strong> above.
          </Typography>
        )}

        {deal.escrow_available && deal.deal_status === 'ACTIVE' && !deal.escrow_id && isViewerBuyer && (
          contractCompleted ? (
            <Button
              variant="outlined"
              onClick={() => navigate(`/deals/${dealId}/escrow`)}
              aria-label="Set up escrow for this deal"
            >
              Set Up Escrow
            </Button>
          ) : (
            <Alert severity="warning" sx={{ width: '100%' }}>
              Escrow cannot be set up until the contract has been signed by both parties and approved by admin.
              {deal.contract_status ? ` Contract status: ${deal.contract_status}` : ' No contract initiated yet.'}
            </Alert>
          )
        )}

        {deal.deal_status === 'CLOSED' && !alreadyRated && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate(`/ratings/submit/${dealId}`)}
            aria-label="Submit rating for this deal"
          >
            Rate this deal
          </Button>
        )}
      </Box>

      <Dialog open={!!rejectDialogMilestone} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject milestone{rejectDialogMilestone ? ` #${rejectDialogMilestone.sequence_order}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Explain what needs to be fixed. The bidder will see this reason and can resubmit their work.
          </Typography>
          <TextField
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              setRejectError('');
            }}
            fullWidth
            required
            multiline
            rows={4}
            error={!!rejectError}
            helperText={rejectError || 'At least 10 characters required.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleMilestoneReject}
            disabled={milestoneLoadingId === rejectDialogMilestone?.id}
          >
            {milestoneLoadingId === rejectDialogMilestone?.id ? <CircularProgress size={20} /> : 'Reject & send back'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
